import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import {
    AWSRunnerAdditionalFieldsKeys,
    getConnectionTypeText,
    useApiEndpointHost,
} from "@/components/runners/details/runner-configuration-keys";
import { RunnerPhaseTag } from "@/components/runners/RunnerPhaseTag";
import { Tooltip } from "@/components/Tooltip";
import { useOrganization } from "@/queries/organization-queries";
import { type PlainRunner } from "@/queries/runner-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { RunnerPhase, RunnerVariant } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useMemo, type FC, type ReactNode } from "react";

export const CloudFormationStackDetails: FC<{ runner: PlainRunner }> = ({ runner }) => {
    const dict = Object.fromEntries(runner.status?.additionalInfo.map((info) => [info.key, info.value]) || []);
    const { data: org } = useOrganization();

    const accountID = dict[AWSRunnerAdditionalFieldsKeys.AWSAccountID] || "";
    const url = dict[AWSRunnerAdditionalFieldsKeys.StackURL] || "";
    const name = dict[AWSRunnerAdditionalFieldsKeys.StackName] || "";
    const region = dict[AWSRunnerAdditionalFieldsKeys.Region] || "";
    const logURL = dict[AWSRunnerAdditionalFieldsKeys.LogURL] || "";
    const customProxyDomain = dict[AWSRunnerAdditionalFieldsKeys.RunnerProxyDomain];
    const runnerTemplateBuildVersion = dict[AWSRunnerAdditionalFieldsKeys.RunnerTemplateBuildVersion];
    const usingPrivateConnection = dict[AWSRunnerAdditionalFieldsKeys.UsingPrivateConnection] === "true";
    const apiEndpoint = dict[AWSRunnerAdditionalFieldsKeys.ApiEndpoint] || "";

    const gatewayHost = useMemo(() => {
        const gatewayInfo = runner.status?.gatewayInfo?.gateway;
        if (!gatewayInfo) {
            return null;
        }

        try {
            const url = new URL(gatewayInfo.url);
            return url.host;
        } catch (e) {
            console.error("Invalid gateway URL:", gatewayInfo.url, e);
            return gatewayInfo.url;
        }
    }, [runner.status?.gatewayInfo]);

    const apiEndpointHost = useApiEndpointHost(apiEndpoint);

    return (
        <div className="flex flex-col gap-2 overflow-x-hidden p-[1px]">
            {runner && (
                <Info label="Status">
                    <RunnerPhaseTag runner={runner} />
                </Info>
            )}
            <DegradationReason runner={runner} />
            {accountID && <Info label="AWS Account ID">{accountID}</Info>}
            {name && <Info label="Stack name">{name}</Info>}
            {runner.spec?.variant ? (
                <Info label="Template">
                    <Pill variant="neutral">
                        <div className="flex items-center gap-1">
                            <span className="font-bold">
                                {runner.spec.variant === RunnerVariant.ENTERPRISE ? "Enterprise" : "Standard"}
                            </span>
                            <Tooltip
                                content={
                                    <div className="font-normal">
                                        <span>The CloudFormation template that was used to create this runner.</span>{" "}
                                        <ExternalLink
                                            href="https://ona.com/docs/ona/runners/aws/overview#choose-your-aws-runner"
                                            className="text-sm"
                                            iconSize="sm"
                                        >
                                            Learn more
                                        </ExternalLink>
                                    </div>
                                }
                            >
                                <button>
                                    <IconInfo size="sm" />
                                    <span className="sr-only">More details</span>
                                </button>
                            </Tooltip>
                        </div>
                    </Pill>
                </Info>
            ) : null}
            {runnerTemplateBuildVersion && <Info label="Template build version">{runnerTemplateBuildVersion}</Info>}

            <Info label="Runner name">{runner.name}</Info>
            <Info label="Runner version">{runner.status?.version}</Info>
            {region && <Info label="Runner region">{region}</Info>}
            <Info label="Connection type">
                <div className="flex items-center gap-1">
                    <Text className="shrink truncate text-base font-bold">
                        {getConnectionTypeText(apiEndpointHost, usingPrivateConnection)}
                    </Text>
                    {!usingPrivateConnection ? (
                        <>
                            {org?.tier !== OrganizationTier.ENTERPRISE ? (
                                <Tooltip
                                    content={
                                        <div className="font-normal">
                                            <span>Upgrade to the Enterprise tier to use VPC endpoints.</span>{" "}
                                            <ExternalLink
                                                href="https://ona.com/pricing"
                                                className="text-sm"
                                                iconSize="sm"
                                            >
                                                Learn more
                                            </ExternalLink>
                                        </div>
                                    }
                                >
                                    <button>
                                        <IconInfo size="sm" />
                                        <span className="sr-only">Upgrade to Enterprise for VPC endpoints</span>
                                    </button>
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    content={
                                        <div className="font-normal">
                                            <span>This runner is connected through the public internet.</span>{" "}
                                            <ExternalLink
                                                href="https://ona.com/docs/ona/runners/aws/overview"
                                                className="text-sm"
                                                iconSize="sm"
                                            >
                                                Learn more
                                            </ExternalLink>{" "}
                                            about setting up a VPC endpoint.
                                        </div>
                                    }
                                >
                                    <button>
                                        <IconInfo size="sm" />
                                        <span className="sr-only">Public Internet Connection</span>
                                    </button>
                                </Tooltip>
                            )}
                        </>
                    ) : (
                        <Tooltip
                            content={
                                <div className="font-normal">
                                    <span>
                                        This runner is connected via a VPC endpoint.{" "}
                                        <ExternalLink
                                            href="https://ona.com/docs/ona/runners/aws/overview"
                                            className="text-sm"
                                            iconSize="sm"
                                        >
                                            Learn more
                                        </ExternalLink>
                                    </span>
                                </div>
                            }
                        >
                            <button>
                                <IconInfo size="sm" />
                                <span className="sr-only">VPC Endpoint Connection</span>
                            </button>
                        </Tooltip>
                    )}
                </div>
            </Info>
            {customProxyDomain ? (
                <Info label="Proxy domain">{customProxyDomain}</Info>
            ) : gatewayHost ? (
                <Info label="Proxy host">{`${gatewayHost} (Ona Gateway)`}</Info>
            ) : null}

            <div className="mt-2 flex gap-2 p-[1px]">
                <Button variant="secondary" className="w-fit" asChild>
                    <ExternalLink href={url} className="flex items-center truncate text-content-primary" iconSize="sm">
                        CloudFormation Stack
                    </ExternalLink>
                </Button>

                <Button variant="secondary" className="w-fit" asChild>
                    <ExternalLink
                        href={logURL}
                        className="flex items-center truncate text-content-primary"
                        iconSize="sm"
                    >
                        Runner Logs
                    </ExternalLink>
                </Button>
            </div>
        </div>
    );
};

export const Info: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
    <div className="flex flex-row flex-wrap items-center gap-2">
        <Text className="min-w-max text-base">{label}: </Text>
        {typeof children === "string" ? (
            <Text className="shrink truncate text-base font-bold">{children}</Text>
        ) : (
            <div className="shrink">{children}</div>
        )}
    </div>
);

export const DegradationReason: FC<{ runner: PlainRunner }> = ({ runner }) => {
    const isDegraded = runner.status?.phase === RunnerPhase.DEGRADED;
    const degradationReason = runner.status?.message;

    if (!isDegraded || !degradationReason) {
        return null;
    }

    return (
        <div className="flex flex-col items-center gap-1 md:flex-row md:items-start md:gap-2">
            <Text className="min-w-max text-base">Reason: </Text>
            <Text className="shrink whitespace-pre-wrap break-words text-base font-bold text-content-negative">
                {degradationReason}
            </Text>
        </div>
    );
};
